import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DEV_MODE } from "@/lib/dev-mode";

/**
 * Single source of truth for the onboarding walkthrough.
 *
 * Completion is stored on `profiles.onboarding_completed_at` (NULL = never
 * finished). The dashboard auto-opens the tour only for first-time users; the
 * Settings card and sidebar "Product Tour" entry replay it on demand without
 * clearing the stored timestamp.
 */
export const useOnboarding = () => {
  const [autoShow, setAutoShow] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        if (DEV_MODE) {
          // Dev previews have no persistent account: keep it local-only.
          if (!localStorage.getItem("onboarding_dismissed")) {
            if (!cancelled) setAutoShow(true);
          }
          return;
        }

        const { data, error } = await supabase
          .from("profiles")
          .select("onboarding_completed_at")
          .eq("id", user.id)
          .maybeSingle();

        // On error we deliberately do NOT open the tour — a failed read must
        // never turn into a modal that nags on every page load.
        if (error) {
          console.warn("Onboarding status read failed:", error.message);
          return;
        }
        if (!cancelled && data && !data.onboarding_completed_at) setAutoShow(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, []);

  /** Called when the walkthrough is finished or skipped. */
  const complete = useCallback(async () => {
    setAutoShow(false);
    setReplaying(false);

    if (DEV_MODE) {
      localStorage.setItem("onboarding_dismissed", "true");
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ onboarding_completed_at: new Date().toISOString() })
      .eq("id", user.id);

    if (error) console.error("Failed to record onboarding completion:", error.message);
  }, []);

  const replay = useCallback(() => setReplaying(true), []);

  return { isOpen: autoShow || replaying, loading, complete, replay };
};

/** Event used to trigger a replay from anywhere in the app. */
export const REPLAY_TOUR_EVENT = "veylo:replay-tour";

export const requestTourReplay = () =>
  window.dispatchEvent(new CustomEvent(REPLAY_TOUR_EVENT));
